import { ContentChefClient } from "@contentchef/contentchef-management-js-client";
import { MediaGalleryId, MediaProvider, MediaStatus, MediaType } from "@contentchef/contentchef-types";
import axios from "axios";
import { action, IObservableArray, observable, runInAction } from "mobx";
import { CLOUDINARY_RAW, getFileNameAndExtension, getFileType } from "../../services/cloudinaryUtils";
import { debounce, waitFor } from "../../services/utils";

export class Media {
  file: File;
  @observable fileName: string = "";
  fileExt: string = "";
  fileType: MediaType;
  mediaPreview: string = "";
  @observable validMediaPublicId: boolean | null = null;
  @observable tags: IObservableArray<string> = observable.array([]);
  debouncedValidPublicId: Function;
  @observable suggestedTags: IObservableArray<string> = observable.array([]);
  @observable loader: {
    suggestedTags: boolean;
    uploading: boolean;
    validatingPublicId: boolean;
  } = {
    suggestedTags: false,
    uploading: false,
    validatingPublicId: false,
  };

  @observable errors: { upload: boolean } = {
    upload: false,
  };

  @observable action: { uploaded: boolean } = {
    uploaded: false,
  };

  @observable readyToUpload: boolean = false;

  constructor(
    private readonly api: ContentChefClient,
    private mediaGalleryId: MediaGalleryId,
    // all folders with rootFolder excluded
    private folderPath: string = "",
    file: File,
  ) {
    const { name, ext } = getFileNameAndExtension(file);
    this.file = file;
    this.fileType = getFileType(file.type);
    this.fileName = name;
    this.fileExt = ext;
    this.mediaPreview = window.URL.createObjectURL(file);
    this.debouncedValidPublicId = debounce(this.isValidMediaPublicId);

    this.isValidMediaPublicId(this.fileType === CLOUDINARY_RAW ? file.name : name, this.fileType);
  }

  get fileNameAndExtension() {
    return `${this.fileName}.${this.fileExt}`;
  }

  get fullPublicId() {
    return `${this.folderPath}/${this.fileName}`;
  }

  @action
  async validate() {
    this.readyToUpload = false;
    this.isValidMediaPublicId(this.fileNameAndExtension, this.fileType as MediaType);
  }

  @action
  setFileName(fileName: string) {
    this.fileName = fileName;
    this.validMediaPublicId = false;
    this.loader.validatingPublicId = true;
    if (fileName.length > 0) {
      if (getFileType(this.file.type) === CLOUDINARY_RAW) {
        this.debouncedValidPublicId(this.fileNameAndExtension, this.fileType);
      } else {
        this.debouncedValidPublicId(this.fileName, this.fileType);
      }
    }
  }

  @action
  async setSuggestedTags(partialTag: string) {
    try {
      this.loader.suggestedTags = true;
      const response = await this.api.media.listTag({
        tag: partialTag,
      });
      runInAction(() => {
        this.suggestedTags.replace(response.tags);
        this.loader.suggestedTags = false;
      });
    } catch (e) {
      console.log("Error occurred trying to retrieve media tag list", e);
      runInAction(() => {
        this.loader.suggestedTags = false;
      });
    }
  }

  @action
  async setTags(tags: string[]) {
    this.tags.replace(tags);
  }

  @action
  async upload(waitForUpload: boolean = false) {
    this.loader.uploading = true;
    this.errors.upload = false;

    try {
      const uploadUrlResponse = await this.getUploadUrl();
      const { uploadUrl, readUrl, writeUrl, mediaPublicId, type, mediaId } = uploadUrlResponse;

      // The endpoint answers for either provider: cloudflare takes the bytes in
      // one signed PUT, cloudinary keeps the FormData dance. Default to
      // cloudinary so a provider we don't know about still uploads.
      let result;
      if (type === MediaProvider.CLOUDFLARE) {
        result = await this.uploadToCloudflare(uploadUrl, mediaPublicId);
      } else if (uploadUrlResponse.largeUpload) {
        result = await this.largeUpload(uploadUrl, readUrl!, writeUrl!, mediaPublicId);
      } else {
        result = await this.uploadToCloudinary(uploadUrl, mediaPublicId);
      }

      if (waitForUpload) {
        let uploaded = false;
        while (!uploaded) {
          await waitFor(2000);
          try {
            // Looked up by id — the endpoint no longer takes parentFolder +
            // mediaPublicId. The row exists as soon as the upload is requested,
            // so wait for the provider to actually process the bytes.
            const mediaResponse = await this.api.media.get({
              mediaGalleryId: this.mediaGalleryId,
              mediaId,
            });
            if (mediaResponse.mediaPublicId && mediaResponse.status !== MediaStatus.QUEUED) {
              uploaded = true;
            }
          } catch (error) {
            console.log("File not yet uploaded. waiting...");
          }
        }
      }

      runInAction(() => {
        this.loader.uploading = false;
        this.errors.upload = false;
        this.action.uploaded = true;
      });

      return result;
    } catch (error) {
      console.log(error);
      runInAction(() => {
        this.loader.uploading = false;
        this.errors.upload = true;
      });
      return Promise.reject({ error, fileName: this.fileName });
    }
  }

  private async largeUpload(uploadUrl: string, readUrl: string, writeUrl: any, fullMediaPublicId: string) {
    try {
      await axios.put(writeUrl, this.file);
      return await this.uploadToCloudinary(uploadUrl, fullMediaPublicId, readUrl);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  // Cloudflare hands back a single signed url that takes the raw bytes, and
  // answers with no public url — the only consumer of this result reads
  // `fileName`, and the media's url is resolved from the gallery afterwards.
  private async uploadToCloudflare(uploadUrl: string, fullMediaPublicId: string) {
    try {
      await axios.put(uploadUrl, this.file, { headers: { "Content-Type": this.file.type } });
      return { url: "", mediaPublicId: fullMediaPublicId, fileName: this.fileName };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  private async uploadToCloudinary(uploadUrl: string, fullMediaPublicId: string, readUrl?: string) {
    const formData = new FormData();
    if (readUrl) {
      formData.append("file", readUrl);
    } else {
      formData.append("file", this.file);
    }

    try {
      const result = await axios.post(uploadUrl, formData);
      return { url: result.data.secure_url, mediaPublicId: fullMediaPublicId, fileName: this.fileName };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  private async getUploadUrl() {
    const fileType = getFileType(this.file.type);
    const requestParams = {
      mediaType: fileType,
      mediaPublicId: this.fileName,
      mediaGalleryId: this.mediaGalleryId,
      parentFolder: this.folderPath,
      size: this.file.size,
      tags: this.tags,
      // The upload endpoint signs the url against the mime type.
      mimeType: this.file.type,
    };

    try {
      return await this.api.media.upload(requestParams);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  private async isValidMediaPublicId(fileName: string, mediaType: MediaType) {
    try {
      const response = await this.api.media.verify({
        mediaGalleryId: this.mediaGalleryId,
        mediaType,
        mediaPublicId: fileName,
        parentFolder: `${this.folderPath}`,
      });
      runInAction(() => {
        this.validMediaPublicId = response.valid;
        this.loader.validatingPublicId = false;
        this.readyToUpload = response.valid;
      });
    } catch (e) {
      console.log("Error occurred trying to valid mediaPublicId");
      runInAction(() => {
        this.validMediaPublicId = false;
        this.loader.validatingPublicId = false;
      });
    }
  }
}
